import { Button, Footer, Modal } from '@narvar/hum';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route } from 'react-router';
import { Switch, useLocation } from 'react-router-dom';
import { ModalBody, ModalFooter } from 'reactstrap';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import { fetchRetailerSettings } from '../../actions/exceptionAnalyticsActions';
import {
  fetchDashboards,
  fetchReportDefinitions,
} from '../../actions/reportingActions';
import { fetchStatus, fetchSelf } from '../../actions/userActions';
import { MONITOR_PRODUCT } from '../../constants/monitor/configurations';
import { CSM_LOGOUT, RETAILER_SWITCH } from '../../constants/storage';
import useInitialiseChurnZeroInfo from '../../hooks/useInitialiseChurnZeroInfo';
import useInitialiseFullStoryInfo from '../../hooks/useInitialiseFullStoryInfo';
import getRoleBasedLandingPage from '../../lib/getRoleBasedLandingPage';
import isHubAccessBlocked from '../../lib/isHubAccessBlocked';
import isNarvarInternalUser from '../../lib/isNarvarInternalUser';
import Forbidden from '../Forbidden';
import MigrationTakeover from '../MigrationTakeover';
import Navigation from '../Navigation';
import Layout from './Layout';
import routes from './routes';
import theme from '../../theme';

const Authorized = () => {
  const [isCsmStateOutOfSync, setCsmStateOutOfSync] = useState(false);
  const [
    isSwitchRetailerStateOutOfSync,
    setSwitchRetailerStateOutOfSync,
  ] = useState(false);
  const dispatch = useDispatch();
  const {
    user,
    profile,
    isCsm,
    retailerId,
    retailerIdToRetailerInfo,
    reporting: { hasFetchedDashboards } = {},
    exceptionsFeatureEnabled,
  } = useSelector(
    ({ userReducer, reporting, exceptionsAnalyticsReducer, uiReducer }) => ({
      user: userReducer.user,
      profile: userReducer.profileUser,
      isCsm: userReducer.user ? userReducer.user.customerSupportLogin : false,
      retailerId: userReducer.user ? userReducer.user.retailerId : undefined,
      retailerIdToRetailerInfo: userReducer.user
        ? userReducer.user.retailerIdToRetailerInfo
        : {},
      reporting,
      isNavOpen: uiReducer.isNavOpen,
      exceptionsFeatureEnabled:
        exceptionsAnalyticsReducer.exceptionsFeatureEnabled,
    }),
  );
  const location = useLocation();

  useInitialiseChurnZeroInfo(user, retailerIdToRetailerInfo[retailerId]);
  useInitialiseFullStoryInfo(user, profile);

  const { t, i18n } = useTranslation('hum');

  useEffect(() => {
    if (!user) {
      dispatch(fetchStatus());
    }
  }, [user, dispatch]);

  useEffect(() => {
    dispatch(fetchSelf());
  }, [profile, dispatch]);

  useEffect(() => {
    if (
      exceptionsFeatureEnabled === null ||
      typeof exceptionsFeatureEnabled === 'undefined'
    ) {
      dispatch(fetchRetailerSettings());
    }
  }, [dispatch, exceptionsFeatureEnabled]);

  useEffect(() => {
    const handleStorage = event => {
      if (event.key === CSM_LOGOUT && isCsm) {
        setCsmStateOutOfSync(true);
      } else if (event.key === RETAILER_SWITCH) {
        setSwitchRetailerStateOutOfSync(true);
      }
    };

    window.addEventListener('storage', handleStorage);

    return () => window.removeEventListener('storage', handleStorage);
  }, [isCsm, retailerId]);

  useEffect(() => {
    if (user) {
      const { products = [] } = retailerIdToRetailerInfo[retailerId];
      if (products.includes(MONITOR_PRODUCT) && !hasFetchedDashboards) {
        dispatch(fetchDashboards());
        dispatch(fetchReportDefinitions());
      }
    }
  }, [
    dispatch,
    hasFetchedDashboards,
    retailerId,
    retailerIdToRetailerInfo,
    user,
  ]);

  const handleLocaleChange = async locale => {
    await i18n.changeLanguage(locale);
  };

  const homepageEnabled = useFeatureIsOn('hub-homepage');

  if (!user) return null;

  const isNarvarAdmin = user.rolesList.some(
    role => role.name === 'NARVAR_SUPER_ADMIN' || role.name === 'NARVAR_ADMIN',
  );

  // Only narvar admin can see locales still in development
  const availableLocales = isNarvarAdmin
    ? ['en', 'es', 'fr', 'it', 'ja']
    : ['en', 'ja'];

  /*
   * Deliberately not the migration variant the banner reads (CPMT-5928): being
   * live in Command no longer implies losing Hub, so hub-backend decides the
   * takeover on its own flag and a retailer can be live in both for a while.
   */
  const { migration } = retailerIdToRetailerInfo[retailerId] || {};
  const showMigrationTakeover =
    isHubAccessBlocked(migration) && !isNarvarInternalUser(user);

  return (
    <div>
      <Helmet
        defaultTitle="Narvar Hub"
        htmlAttributes={{ lang: 'en' }}
        meta={[
          { charset: 'utf-8' },
          { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
          { name: 'description', content: '' },
          {
            name: 'viewport',
            content: 'width=device-width, initial-scale=1',
          },
        ]}
        link={[
          { rel: 'icon', href: require('~src/assets/images/favicon.png') }, // eslint-disable-line global-require
          {
            rel: 'apple-touch-icon',
            href: require('~src/assets/images/apple-touch-icon.png'), // eslint-disable-line global-require
          },
          {
            rel: 'apple-touch-icon',
            sizes: '72x72',
            href: require('~src/assets/images/apple-touch-icon-72x72.png'), // eslint-disable-line global-require
          },
          {
            rel: 'apple-touch-icon',
            sizes: '114x114',
            href: require('~src/assets/images/apple-touch-icon-114x114.png'), // eslint-disable-line global-require
          },
        ]}
      />
      {showMigrationTakeover ? (
        <MigrationTakeover />
      ) : (
        <>
          <Navigation />
          <div
            style={{
              transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              background: location.pathname === '/home' ? 'white' : 'inherit',
            }}
          >
            <Switch>
              <Route
                exact
                path="/"
                render={() => {
                  if (!user) {
                    return <Redirect to="/" />;
                  }

                  const { external, url } = getRoleBasedLandingPage(
                    user,
                    homepageEnabled,
                  );

                  if (external) {
                    window.location.assign(url);
                    return null;
                  }

                  return <Redirect to={url} />;
                }}
              />
              {routes.map(route => (
                <Layout
                  /*
                   * A route may declare an `authority` predicate; if the
                   * signed-in user does not satisfy it, the route still
                   * matches but renders Forbidden instead of the real page
                   * (CPMT-5077). `user` is guaranteed here — this block is
                   * past the `if (!user) return null` guard above — so the
                   * predicate never runs against a half-loaded user.
                   */
                  component={
                    route.authority && !route.authority(user)
                      ? Forbidden
                      : route.component
                  }
                  key={route.path || '404'}
                  exact={route.exact}
                  path={route.path}
                  beacon={route.beacon}
                  route={Route}
                />
              ))}
            </Switch>
            <Footer
              i18n={t('footer', { returnObjects: true })}
              locales={availableLocales}
              showLocale
              currentLocale={i18n.resolvedLanguage}
              onLocaleChange={handleLocaleChange}
            />
          </div>
        </>
      )}
      {/* Outside the takeover ternary: the takeover's own switcher fires
          RETAILER_SWITCH in other tabs, so blocked tabs need this prompt too
          or they stay blocked on the stale retailer. */}
      <Modal
        isOpen={isCsmStateOutOfSync || isSwitchRetailerStateOutOfSync}
        toggle={() => window.location.reload()}
        title="You are logged in as a different retailer"
      >
        <ModalBody>
          You switched to a different retailer account on another open tab. You
          will now be logged in to this page as that retailer.
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            isModal
            onClick={() => window.location.reload()}
          >
            GOT IT
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Authorized;
